import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export enum LogLevel {
  Error = 0,
  Warn = 1,
  Info = 2,
  Debug = 3
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
  source?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private logLevel: LogLevel = environment.production ? LogLevel.Error : (environment.debug ? LogLevel.Debug : LogLevel.Info);
  private logs: LogEntry[] = [];
  private maxLogs = 1000;

  error(message: string, data?: unknown, source?: string): void {
    this.log(LogLevel.Error, message, data, source);
  }

  warn(message: string, data?: unknown, source?: string): void {
    this.log(LogLevel.Warn, message, data, source);
  }

  info(message: string, data?: unknown, source?: string): void {
    this.log(LogLevel.Info, message, data, source);
  }

  debug(message: string, data?: unknown, source?: string): void {
    this.log(LogLevel.Debug, message, data, source);
  }

  private log(level: LogLevel, message: string, data?: unknown, source?: string): void {
    if (level > this.logLevel) {
      return;
    }

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      source
    };

    this.logs.push(logEntry);

    // Keep only the last N logs to prevent memory issues
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output
    this.outputToConsole(logEntry);

    // In production, you might want to send to external logging service
    if (environment.production && level <= LogLevel.Warn) {
      this.sendToExternalService(logEntry);
    }
  }

  private outputToConsole(entry: LogEntry): void {
    if (!environment.logging) return;

    const logMessage = `[${entry.timestamp}] ${entry.source ? `[${entry.source}] ` : ''}${entry.message}`;

    switch (entry.level) {
      case LogLevel.Error:
        console.error(logMessage, entry.data);
        break;
      case LogLevel.Warn:
        console.warn(logMessage, entry.data);
        break;
      case LogLevel.Info:
        console.info(logMessage, entry.data);
        break;
      case LogLevel.Debug:
        console.debug(logMessage, entry.data);
        break;
    }
  }

  private sendToExternalService(entry: LogEntry): void {
    // TODO: Implement external logging service integration
    // This could be Sentry, LogRocket, DataDog, etc.
    console.log('Would send to external logging service:', entry);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  getLogsSince(timestamp: Date): LogEntry[] {
    return this.logs.filter(log => new Date(log.timestamp) >= timestamp);
  }

  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}